import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@features/auth/hooks/useAuth';
import { UserRole } from '@features/auth/types';
import { usePermissions } from '@shared/hooks/usePermissions';
import { CircularProgress, Box } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  /** Any one of these permission keys is sufficient to grant access */
  allowedPermissions?: string[];
  requireAuth?: boolean;
}

/**
 * Protected Route Component
 * Handles authentication and role-based access control
 * 
 * @param children - Components to render if authorized
 * @param allowedRoles - Array of roles that can access this route
 * @param requireAuth - Whether authentication is required (default: true)
 */
export const ProtectedRoute = ({
  children,
  allowedRoles,
  allowedPermissions,
  requireAuth = true,
}: ProtectedRouteProps) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const location = useLocation();

  console.log('🔒 ProtectedRoute:', { isAuthenticated, isLoading, user, requireAuth, location: location.pathname });

  // Show loading spinner while checking auth
  if (isLoading) {
    console.log('⏳ ProtectedRoute: Loading...');
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Check if authentication is required
  if (requireAuth && !isAuthenticated) {
    console.log('🚫 ProtectedRoute: Not authenticated, redirecting to login');
    // Redirect to login, saving the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if roles are specified
  if (allowedRoles && allowedRoles.length > 0) {
    if (!user) {
      console.log('🚫 ProtectedRoute: No user, redirecting to login');
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    const hasRequiredRole = allowedRoles.includes(user.role as UserRole);
    const hasRequiredPermission =
      allowedPermissions && allowedPermissions.length > 0
        ? hasAnyPermission(allowedPermissions)
        : false;

    if (!hasRequiredRole && !hasRequiredPermission) {
      console.log('🚫 ProtectedRoute: Insufficient permissions, redirecting to unauthorized');
      // User is authenticated but doesn't have the required role or permission
      return <Navigate to="/unauthorized" replace />;
    }
  }

  console.log('✅ ProtectedRoute: Authorized, rendering children');
  // User is authorized, render the protected content
  return <>{children}</>;
};
